import { c as _c } from "react/compiler-runtime";
import { dehydrate, Hydrate, QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { LegContentRecord } from '~/libs/dato/fragments/legContentFragment';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { TripOverview } from '~/services/engine-ts';
type Props = {
  tripId: string;
  overviewData: TripOverview;
  children: ReactNode;
};
const makeOverviewQueryKey = (tripId: string) => [['trip', 'getOverview'], {
  input: {
    tripId
  },
  type: 'query'
}] as const;
export const makeHeroRoutesCmsKey = (tripId: string) => ['heroRoutesCms', tripId] as const;
export const makePlaceImagesCmsKey = (tripId: string) => ['placeImagesCms', tripId] as const;
export const makeLegContentCmsKey = (tripId: string) => ['legContentCms', tripId] as const;
export const HydratedOverview = t0 => {
  const $ = _c(8);
  const {
    tripId,
    overviewData,
    children
  } = t0;
  let T0;
  let t1;
  if ($[0] !== overviewData || $[1] !== tripId) {
    const queryClient = new QueryClient();
    queryClient.setQueryData(makeOverviewQueryKey(tripId), overviewData);
    T0 = Hydrate;
    t1 = dehydrate(queryClient);
    $[0] = overviewData;
    $[1] = tripId;
    $[2] = T0;
    $[3] = t1;
  } else {
    T0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== T0 || $[5] !== children || $[6] !== t1) {
    t2 = <T0 state={t1}>{children}</T0>;
    $[4] = T0;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
type HeroRoutesProps = {
  tripId: string;
  heroRoutesCms: Record<string, HeroRouteRecord>;
  placeImagesCms?: Record<string, PlaceImageRecord>;
  legContentCms?: Record<string, LegContentRecord>;
  children: ReactNode;
};
export const HydratedHeroRoutes = t0 => {
  const $ = _c(10);
  const {
    tripId,
    heroRoutesCms,
    placeImagesCms,
    legContentCms,
    children
  } = t0;
  let T0;
  let t1;
  if ($[0] !== heroRoutesCms || $[1] !== legContentCms || $[2] !== placeImagesCms || $[3] !== tripId) {
    const queryClient = new QueryClient();
    queryClient.setQueryData(makeHeroRoutesCmsKey(tripId), heroRoutesCms);
    if (placeImagesCms) {
      queryClient.setQueryData(makePlaceImagesCmsKey(tripId), placeImagesCms);
    }
    if (legContentCms) {
      queryClient.setQueryData(makeLegContentCmsKey(tripId), legContentCms);
    }
    T0 = Hydrate;
    t1 = dehydrate(queryClient);
    $[0] = heroRoutesCms;
    $[1] = legContentCms;
    $[2] = placeImagesCms;
    $[3] = tripId;
    $[4] = T0;
    $[5] = t1;
  } else {
    T0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== T0 || $[7] !== children || $[8] !== t1) {
    t2 = <T0 state={t1}>{children}</T0>;
    $[6] = T0;
    $[7] = children;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};